import { RegistrationFlowField, RegistrationFlowOrchestration } from '../types/apiConfig';
import {
  CiamToImsGenderCodeMapping,
  GENDER_CODE_CIAM,
  PatchImsUserRequest,
} from '../types/registrationFlows';

/* eslint-disable @typescript-eslint/no-non-null-assertion */
export class PatchRequestBuilder {
  private readonly orchestration: RegistrationFlowOrchestration;
  private readonly request: PatchImsUserRequest = {};

  constructor(orchestration: RegistrationFlowOrchestration) {
    this.orchestration = orchestration;
  }

  withFirstName(firstName: string | undefined): PatchRequestBuilder {
    if (!this.shouldInclude(firstName, RegistrationFlowField.FIRST_NAME)) return this;
    this.request.namesPerson = {
      ...this.request.namesPerson,
      firstName: this.coalesce(firstName!),
    };
    return this;
  }

  withLastName(lastName: string | undefined): PatchRequestBuilder {
    if (!this.shouldInclude(lastName, RegistrationFlowField.LAST_NAME)) return this;
    this.request.namesPerson = {
      ...this.request.namesPerson,
      lastName: this.coalesce(lastName!),
    };
    return this;
  }

  withGender(gender: GENDER_CODE_CIAM | undefined): PatchRequestBuilder {
    if (!this.shouldInclude(gender, RegistrationFlowField.GENDER)) return this;
    this.request.demographicsPerson = {
      ...this.request.demographicsPerson,
      genderCode: CiamToImsGenderCodeMapping[gender!],
    };
    return this;
  }

  withGenderCustom(genderCustom: string | undefined): PatchRequestBuilder {
    if (!this.shouldInclude(genderCustom, RegistrationFlowField.GENDER)) return this;
    this.request.demographicsPerson = {
      ...this.request.demographicsPerson,
      genderCustom: this.coalesce(genderCustom!),
    };
    return this;
  }

  withBirthDate(
    day: string | undefined,
    month: string | undefined,
    year: string | undefined
  ): PatchRequestBuilder {
    if (!this.orchestration.visibleFields.includes(RegistrationFlowField.BIRTH_DATE)) return this;
    if (day === undefined || month === undefined || year === undefined) return this;
    this.request.demographicsPerson = {
      ...this.request.demographicsPerson,
      dateOfBirth: this.formatDateOfBirth(day, month, year),
    };
    return this;
  }

  withPhoneNumber(phoneNumber: string | undefined): PatchRequestBuilder {
    if (!this.shouldInclude(phoneNumber, RegistrationFlowField.PHONE_NUMBER)) return this;
    this.request.contactpointsPhone = {
      ...this.request.contactpointsPhone,
      phone: this.coalesce(phoneNumber!),
    };
    return this;
  }

  withCity(city: string | undefined): PatchRequestBuilder {
    if (!this.shouldInclude(city, RegistrationFlowField.CITY)) return this;
    this.request.address = {
      ...this.request.address,
      city: this.coalesce(city!),
    };
    return this;
  }

  withStreet(street: string | undefined): PatchRequestBuilder {
    if (!this.shouldInclude(street, RegistrationFlowField.STREET)) return this;
    this.request.address = {
      ...this.request.address,
      street: this.coalesce(street!),
    };
    return this;
  }

  withPostalCode(postalCode: string | undefined): PatchRequestBuilder {
    if (!this.shouldInclude(postalCode, RegistrationFlowField.POST_CODE)) return this;
    this.request.address = {
      ...this.request.address,
      postalCode: this.coalesce(postalCode!),
    };
    return this;
  }

  withBoxNumber(boxNumber: string | undefined): PatchRequestBuilder {
    if (!this.shouldInclude(boxNumber, RegistrationFlowField.BOX_NUMBER)) return this;
    this.request.address = {
      ...this.request.address,
      houseNumberExtension: this.coalesce(boxNumber!),
    };
    return this;
  }

  withHouseNumber(houseNumber: string | undefined): PatchRequestBuilder {
    if (!this.shouldInclude(houseNumber, RegistrationFlowField.HOUSE_NUMBER)) return this;
    this.request.address = {
      ...this.request.address,
      houseNumber: this.coalesce(houseNumber!),
    };
    return this;
  }

  withCountry(country: string | undefined): PatchRequestBuilder {
    if (!this.shouldInclude(country, RegistrationFlowField.COUNTRY)) return this;
    this.request.address = {
      ...this.request.address,
      countryCode: this.coalesce(country!),
    };
    return this;
  }

  asPatchImsUserRequest(): PatchImsUserRequest {
    return this.request;
  }

  private formatDateOfBirth(day: string, month: string, year: string): string | null {
    // Clearing date of birth is currently not possible as IMS does not accept an empty string in this property
    // and null values are ignored
    if (day === '' || month === '' || year === '') return null;
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }

  private shouldInclude(value: unknown | undefined, orchestrationField: RegistrationFlowField) {
    return value !== undefined && this.orchestration.visibleFields.includes(orchestrationField);
  }

  private coalesce(value: string): string | null {
    // Temporarily sending empty strings to IMS because that's how values are cleared there
    // When the IMS patch endpoint has been updated, we should send null values instead
    return value === '' ? '' : value;
  }
}
/* eslint-enable @typescript-eslint/no-non-null-assertion */
